<template>
  <div
    ref="rootEl"
    :class="[computedClass, { 'border-black': clickedInside }]"
    @mousedown="clickedInside = true"
    @focusin="clickedInside = true"
    :data-testid="dataTestId[0]"
    class="group">
    <div
      class="flex flex-col sm:flex-row flex-wrap p-2 sm:p-3 gap-2 items-start sm:items-center border-b-2 bg-gray-50"
      :class="borderColorClass">
      <div class="flex flex-wrap gap-1 w-full sm:w-auto">
        <button
          v-for="(action, index) in actions"
          :key="index"
          @click="action.action()"
          :class="[
            'flex items-center justify-center w-8 h-8 rounded-sm hover:bg-gray-200 transition-colors text-sm font-medium',
            { 'bg-gray-200 text-gray-900': action.isActive?.() }
          ]">
          <component
            :is="action.icon"
            :title="action.tooltip"
            class="w-5" />
        </button>
      </div>

      <div class="hidden sm:block h-5 w-px bg-gray-200 mx-1">
      </div>

      <div class="relative w-full sm:w-auto">
        <button
          @click="toggleDropdown"
          :title="'Symbol picker'"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm text-royal-blue-100 hover:bg-gray-200 rounded-sm transition-colors w-full sm:w-auto justify-between sm:justify-start">
          <span class="text-lg font-bold underline">Symbol</span>
          <span class="ml-1 text-xs flex items-center self-center">▼</span>
        </button>
        <div
          v-if="isDropdownOpen"
          class="p-2 absolute z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 w-full md:w-[600px] sm:w-[330px] left-0 sm:left-auto">
          <div class="grid grid-cols-12">
            <button
              v-for="char in specialCharacters"
              :key="char"
              @click="insertCharacter(char)"
              class="sm:h-6 flex items-center justify-center text-[20px] hover:bg-gray-200 rounded-sm font-medium">
              {{ char }}
            </button>
          </div>
        </div>
      </div>

      <div class="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2 sm:gap-3 text-sm">
        <div class="ml-3 md:ml-0 flex items-center gap-2">
          <div
            class="w-2 h-2 rounded-full"
            :class="{
              'bg-lime-100': enableLimit && ((counterType === 'words' && wordCount <= wordLimit) || (counterType === 'characters' && characterCount <= characterLimit)),
              'bg-ruby-red-100': enableLimit && ((counterType === 'words' && wordCount > wordLimit) || (counterType === 'characters' && characterCount > characterLimit)),
            }">
          </div>

          <span
            v-if="props.counterType === 'words'"
            :id="wordCountId"
            class="text-lg">
            {{ wordCount }} / {{ wordLimit }} words
          </span>
          <span
            v-if="props.counterType === 'characters'"
            class="text-lg">
            {{ characterCount }} / {{ characterLimit }} characters
          </span>
        </div>
      </div>
    </div>

    <EditorContent
      :id="id"
      :editor="editor"
      :data-testid="dataTestId[1]"
      class="prose prose-sm sm:prose-base md:prose-lg max-w-none p-4"/>

    <BubbleMenu
      v-if="editor && showLinkMenu"
      :editor="editor"
      :tippy-options="{ duration: 100, placement: 'bottom' }"
      class="flex flex-col sm:flex-row items-center rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
      <input
        v-model="linkUrl"
        placeholder="Enter URL"
        class="h-9 w-full sm:w-[280px] px-3 text-sm border border-gray-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 mb-2 sm:mb-0"
        @keydown.enter.prevent="setLink" />
      <button
        @click="setLink"
        class="h-9 text-blue-500 hover:text-blue-700 rounded-md text-sm font-medium transition-colors w-[90px]">
        {{ editor.isActive('link') ? 'Update' : 'Add' }} link
      </button>
    </BubbleMenu>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, watch, onMounted } from 'vue'
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import TextFormatBold from '../ui-components/icons/TextFormatBold.vue'
import TextFormatItalic from '../ui-components/icons/TextFormatItalic.vue'
import TextFormatUnderline from '../ui-components/icons/TextFormatUnderline.vue'
import TextFormatHyperlink from '../ui-components/icons/TextFormatHyperlink.vue'
import ChevronLeft from '../ui-components/icons/ChevronLeft.vue'
import ChevronRight from '../ui-components/icons/ChevronRight.vue'
import TextFormatSuperscript from '../ui-components/icons/TextFormatSuperscript.vue'
import TextFormatSubscript from '../ui-components/icons/TextFormatSubscript.vue'
import TextFormatRemoveStyles from '../ui-components/icons/TextFormatRemoveStyles.vue'

const props = defineProps({
  dataTestId: {
    type: Array,
    default: () => [],
  },
  counterType: {
    type: String,
    default: 'none',
    validator: (value: string) => ['none', 'words', 'characters'].includes(value),
  },
  enableLimit: {
    type: Boolean,
    default: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  wordLimit: {
    type: Number,
    default: 280,
  },
  characterLimit: {
    type: Number,
    default: 1000,
  },
  modelValue: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    required: true,
  },
  placeHolder: {
    type: String,
    required: true,
  },
  disabledOptions: {
    type: Array,
    default: () => [],
  },
  wordCountId: {
    type: String,
    default: '',
  },
  enableWordLimit: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:modelValue', 'current-word-number', 'text-change'])
const wordCount = ref(0)
const characterCount = ref(0)
const linkUrl = ref('')
const showLinkMenu = ref(false)
const isDropdownOpen = ref(false)
var specialCharacters = ['Α', 'Β', 'Γ', 'Δ', 'Ε', 'Ζ', 'Η', 'Θ', 'Ι', 'Κ', 'Λ', 'Μ', 'Ν', 'Ξ', 'Ο', 'Π', 'Ρ', 'Σ', 'Τ', 'Υ', 'Φ', 'Χ', 'Ψ', 'Ω', 'α', 'β', 'γ', 'δ', 'ε', 'ζ', 'η', 'θ', 'ι', 'κ', 'λ', 'μ', 'ν', 'ξ', 'ο', 'π', 'ρ', 'ς', 'σ', 'τ', 'υ', 'φ', 'χ', 'ψ', 'ω', '¡', '〉', '〈', '♂', '♀', '●', '○', '▽', '△', '□', '■', '⇒', '⇐', '↕', '↔', '↓', '→', '↑', '←', '⅓', '™', '€', '…', '‡', '†', '”', '“', '’', '‘', '‖', '©', '§', '¥', '£', '¢', '+', '│', '⊥', '⊙', '⊇', '⊆', '≧', '≦', '≥', '≤', '≡', '≠', '≒', '≈', '≅', '∽', '∼', '∴', '∮', '∬', '∫', '∥', '∠', '∞', '∝', '√', '∗', '−', '∑', '∏', '∉', '∈', '∇', '∂', 'Å', 'Ω', '″', '′', '‰', '÷', '×', '·', '±', '°', '¬', '=', 'À', 'Ÿ', 'š', 'Š', 'œ', 'Œ', 'ě', 'Ě', 'č', 'Č', 'ċ', 'ć', 'Ć', 'ă', 'Ă', 'ā', 'ÿ', 'ý', 'ü', 'û', 'ú', 'ù', 'ø', 'ö', 'õ', 'ô', 'ó', 'ò', 'ñ', 'ï', 'î', 'í', 'ì', 'ë', 'ê', 'é', 'è', 'ç', 'æ', 'å', 'ä', 'ã', 'â', 'á', 'à', 'Ý', 'Ü', 'Û', 'Ú', 'Ù', 'Ø', 'Ö', 'Õ', 'Ô', 'Ó', 'Ò', 'Ñ', 'Ï', 'Î', 'Í', 'Ì', 'Ë', 'Ê', 'É', 'È', 'Ç', 'Æ', 'Å', 'Ä', 'Ã', 'Â', 'Á']
const clickedInside = ref(false)
const rootEl = ref<HTMLElement | null>(null)

const handleClickOutside = (event: MouseEvent) => {
  if (!rootEl.value) return
  if (!rootEl.value.contains(event.target as Node)) {
    clickedInside.value = false
  }
}

const editor = useEditor({
  content: props.modelValue || '',
  editorProps: {
    attributes: {
      class: 'focus:outline-hidden text-graphite',
    },
    handleDOMEvents: {
      dblclick: (_view, event) => {
        if (event.target instanceof HTMLAnchorElement) {
          const { href } = event.target
          linkUrl.value = href || ''
          showLinkMenu.value = true
          return true
        }
        return false
      },
    },
  },
  extensions: [
    StarterKit,
    Underline,
    Superscript,
    Subscript,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited',
      },
      linkOnPaste: true,
      autolink: true,
      protocols: ['http', 'https'],
      validate: href => /^https?:\/\//.test(href),
    }),
    Placeholder.configure({
      placeholder: props.placeHolder,
      emptyEditorClass:
        'before:content-[attr(data-placeholder)] before:absolute before:[color:var(--color-graphite)] before:pointer-events-none',
    }),
  ],
  onUpdate: ({ editor }) => {
    if (!editor) return
    const text = editor.state.doc.textBetween(
      0,
      editor.state.doc.content.size,
      '\n',
      '\n',
    ).replace(/\s+$/g, '')
    wordCount.value = text.split(/\s+/).filter(word => word.length > 0).length
    const textWithoutNewlines = text.replace(/\n/g, '')
    characterCount.value = textWithoutNewlines.length
    const html = sanitisedContent.value
    emit('text-change', { html, text })
    emit('update:modelValue', html)
    emit('current-word-number', wordCount.value)
  },

  onSelectionUpdate: ({ editor }) => {
    const { from, to } = editor.state.selection
    const text = editor.state.doc.textBetween(from, to)

    if (editor.isActive('link')) {
      const { href } = editor.getAttributes('link')
      linkUrl.value = href || ''
      showLinkMenu.value = true
    }
    else if (text.length === 0) {
      linkUrl.value = ''
      showLinkMenu.value = false
    }
  },
})

const actions = computed(() => {
  if (!editor.value) return []
  return [
    {
      icon: TextFormatBold,
      action: () => editor.value?.chain().focus().toggleBold().run(),
      isActive: () => editor.value?.isActive('bold'),
    },
    {
      icon: TextFormatItalic,
      action: () => editor.value?.chain().focus().toggleItalic().run(),
      isActive: () => editor.value?.isActive('italic'),
    },
    {
      icon: TextFormatUnderline,
      action: () => editor.value?.chain().focus().toggleUnderline().run(),
      isActive: () => editor.value?.isActive('underline'),
    },
    {
      icon: TextFormatSubscript,
      action: () => editor.value?.chain().focus().toggleSubscript().run(),
      isActive: () => editor.value?.isActive('subscript'),
    },
    {
      icon: TextFormatSuperscript,
      action: () => editor.value?.chain().focus().toggleSuperscript().run(),
      isActive: () => editor.value?.isActive('superscript'),
    },
    {
      icon: TextFormatRemoveStyles,
      action: () => editor.value?.chain().focus().unsetAllMarks().run(),
    },
    {
      icon: TextFormatHyperlink,
      action: () => {
        if (!editor.value) return
        const selectionEmpty = editor.value.state.selection.empty

        if (editor.value.isActive('link')) {
          editor.value.chain().focus().extendMarkRange('link').unsetLink().run()
          showLinkMenu.value = false
        }
        else if (!selectionEmpty) {
          showLinkMenu.value = true
          const previousUrl = editor.value.getAttributes('link').href
          linkUrl.value = previousUrl || ''
          editor.value.commands.focus()
        }
        else {
          showLinkMenu.value = false
        }
      },
      isActive: () => editor.value?.isActive('link'),
    },
    {
      icon: ChevronLeft,
      tooltip: 'Undo',
      action: () => editor.value?.chain().focus().undo().run(),
    },
    {
      icon: ChevronRight,
      tooltip: 'Redo',
      action: () => editor.value?.chain().focus().redo().run(),
    },
  ].filter(action => !props.disabledOptions.includes(action.icon.props?.title.default))
})

const sanitisedContent = computed(() => {
  if (!editor.value) return ''

  let html = editor.value.getHTML()

  html = html.replace(/<\/?(strong|em|h[1-6]|p)>/g, (match, tag) => {
    switch (tag) {
      case 'strong': return match.startsWith('</') ? '</b>' : '<b>'
      case 'em': return match.startsWith('</') ? '</i>' : '<i>'
      case 'p': return match.startsWith('</') ? '<br>' : ''
      default: return '\n'
    }
  })

  html = html.replace(/\n{2,}/g, '\n')

  if (props.id === 'dasCustomContent') {
    html = html.replace(/<br>$/, '')
  }
  else {
    html = html.replace(/<br\s*\/?>/g, '\n').replace(/\n$/, '')
    html = html.replace(/<a [^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/g, '$1')
  }

  return html.trim()
})

const computedClass = computed(() => {
  if (props.enableWordLimit && wordCount.value > props.wordLimit) {
    return 'border-2 border-ruby-red-100 rounded-sm bg-white w-full mx-auto data-hj-suppress'
  }

  if (clickedInside.value) {
    return 'border-2 border-black rounded-sm bg-white w-full mx-auto data-hj-suppress'
  }

  return props.readonly
    ? 'border-2 border-dark-grey rounded-sm bg-white w-full mx-auto opacity-60 pointer-events-none data-hj-suppress'
    : 'border-2 border-dark-grey hover:border-royal-blue-100 focus-within:border-royal-blue-160 rounded-sm bg-white w-full mx-auto data-hj-suppress'
})

const borderColorClass = computed(() => {
  if (props.enableWordLimit && wordCount.value > props.wordLimit) {
    return 'border-b-ruby-red-100'
  }

  if (clickedInside.value) {
    return 'border-b-black'
  }

  return 'border-b-dark-grey group-hover:border-b-royal-blue-100 group-focus-within:border-b-royal-blue-160'
})

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value
}

const insertCharacter = (char: string) => {
  if (!editor.value) return
  editor.value.chain().focus().insertContent(char).run()
  isDropdownOpen.value = false
}

const closeDropdownOutside = (event: MouseEvent) => {
  const dropdown = (event.target as HTMLElement).closest('.relative')
  if (!dropdown && isDropdownOpen.value) {
    isDropdownOpen.value = false
  }
}

const setLink = () => {
  if (!editor.value) return
  if (linkUrl.value === '') {
    editor.value.chain().focus().extendMarkRange('link').unsetLink().run()
  }
  else {
    const href = linkUrl.value.match(/^https?:\/\//) ? linkUrl.value : `https://${linkUrl.value}`
    editor.value.chain().focus().extendMarkRange('link').setLink({ href }).run()
  }

  linkUrl.value = ''
  showLinkMenu.value = false
  editor.value.commands.focus()
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (!editor.value) return
    const isSame = sanitisedContent.value === newValue
    if (isSame) return
    editor.value.commands.setContent(newValue.replace(/<br>/g, '\n'), false)
    const text = editor.value.getText()
    wordCount.value = text.split(/\s+/).filter(word => word.length > 0).length
    characterCount.value = text.length
  },
)

const convertNewlines = (content: string, toBrTag: boolean) => {
  if (toBrTag) {
    return content.replace(/\n/g, '<br>')
  }
  else {
    return content.replace(/<br\s*\/?>/g, '\n')
  }
}

onMounted(() => {
  if (props.id != 'dasCustomContent') {
    const contentWithBrTag = convertNewlines(props.modelValue, true)
    editor.value?.commands.setContent(contentWithBrTag, false)
  }
  const text = editor.value?.getText() || ''
  wordCount.value = text.split(/\s+/).filter(word => word.length > 0).length
  characterCount.value = text.length
  emit('current-word-number', wordCount.value)
  document.addEventListener('click', closeDropdownOutside)
  document.addEventListener('mousedown', handleClickOutside)
})

onBeforeUnmount(() => {
  editor.value?.destroy()
  document.removeEventListener('click', closeDropdownOutside)
  document.removeEventListener('mousedown', handleClickOutside)
})
</script>
